import { DebugLogger } from '@affine/debug';
import {
  catchErrorInto,
  effect,
  Entity,
  LiveData,
  mapInto,
} from '@toeverything/infra';
import { filter, map, switchMap } from 'rxjs';

import type { PDFPageTextContent, RenderPageOpts } from '../renderer';
import type { PDF } from './pdf';

const logger = new DebugLogger('affine:pdf:page:render');

export class PDFPage extends Entity<{ pdf: PDF; pageNum: number }> {
  readonly pageNum: number = this.props.pageNum;
  bitmap$ = new LiveData<ImageBitmap | null>(null);
  text$ = new LiveData<PDFPageTextContent | null>(null);
  error$ = new LiveData<any>(null);

  // Error handling lives INSIDE the switchMap projection: an error on the
  // outer pipeline would complete the effect for good (catchError -> EMPTY),
  // silently swallowing every later render call on this pooled entity.
  render = effect(
    switchMap((opts: Omit<RenderPageOpts, 'pageNum'>) =>
      this.props.pdf.renderer
        .ob$('render', {
          ...opts,
          pageNum: this.pageNum,
        })
        .pipe(
          map(data => data?.bitmap),
          filter(Boolean),
          mapInto(this.bitmap$),
          catchErrorInto(this.error$, error => {
            logger.error('Failed to render page', error);
          })
        )
    )
  );

  loadText = effect(
    switchMap(() =>
      this.props.pdf.renderer
        .ob$('text', {
          pageNum: this.pageNum,
        })
        .pipe(
          mapInto(this.text$),
          catchErrorInto(this.error$, error => {
            logger.error('Failed to extract page text', error);
          })
        )
    )
  );

  constructor() {
    super();
    this.disposables.push(() => this.render.unsubscribe());
    this.disposables.push(() => this.loadText.unsubscribe());
  }
}
